import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Check, X, ChevronRight, Instagram, Music2 } from 'lucide-react';
import { INSTAGRAM_PLANS, TIKTOK_PLANS } from '../mock';
import { api } from '../api';

const COLUMNS = [
  {
    key: 'ig-starter',
    platform: 'Instagram',
    icon: 'ig',
    name: 'Starter',
    defaultPrice: 29,
    range: '1,500-3,000 Followers',
    highlight: false,
  },
  {
    key: 'ig-pro',
    platform: 'Instagram',
    icon: 'ig',
    name: 'Pro',
    defaultPrice: 55,
    range: '4,000-6,000 Followers',
    highlight: true,
  },
  {
    key: 'tt-scale',
    platform: 'TikTok',
    icon: 'tt',
    name: 'Scale',
    defaultPrice: 149,
    range: '2,000-3,500 Followers /mo',
    highlight: false,
  },
  {
    key: 'tt-elite',
    platform: 'TikTok',
    icon: 'tt',
    name: 'Elite',
    defaultPrice: 249,
    range: '4,500-7,000 Followers /mo',
    highlight: false,
  },
];

const ROWS = [
  {
    label: 'Followers delivered',
    values: ['1.5K – 3K', '4K – 6K', '2K – 3.5K', '4.5K – 7K'],
  },
  {
    label: 'Results start within 1 day',
    values: [true, true, true, true],
  },
  {
    label: 'Higher reach & engagement',
    values: [true, true, true, true],
  },
  {
    label: 'Genuine comments',
    values: [true, true, true, true],
  },
  {
    label: 'Targeted, organic followers',
    values: [true, true, true, true],
  },
  {
    label: 'Guaranteed results or it’s free',
    values: [true, true, true, true],
  },
  {
    label: 'Real-time analytics dashboard',
    values: [true, true, true, true],
  },
  {
    label: 'LA & London team support',
    values: [true, true, false, true],
  },
  {
    label: 'AI-Powered growth engine',
    values: [false, true, true, true],
  },
  {
    label: 'AI-Match™ audience targeting',
    values: [false, true, false, true],
  },
  {
    label: 'Priority support',
    values: [false, true, true, true],
  },
  {
    label: 'Dedicated account manager',
    values: [false, false, false, true],
  },
];

const IconChip = ({ type }) => (
  <span
    className={`inline-flex items-center justify-center w-6 h-6 rounded-md text-white ${
      type === 'ig'
        ? 'bg-gradient-to-br from-purple-500 via-pink-500 to-amber-400'
        : 'bg-[#0a1930]'
    }`}
  >
    {type === 'ig' ? (
      <Instagram className="w-3.5 h-3.5" />
    ) : (
      <Music2 className="w-3.5 h-3.5" />
    )}
  </span>
);

const getInstagramPrice = (planName, fallback) => {
  const plan = INSTAGRAM_PLANS?.find(
    (p) => p.name?.toLowerCase() === planName.toLowerCase()
  );

  return Number(plan?.price ?? fallback);
};

const getTikTokPrice = (planName, fallback) => {
  const plan = TIKTOK_PLANS?.monthly?.find(
    (p) => p.name?.toLowerCase() === planName.toLowerCase()
  );

  return Number(plan?.price ?? fallback);
};

const parseInstagramPricing = (text, fallbackColumns) => {
  const prices = {};

  String(text || '')
    .split('\n')
    .map((line) => line.trim())
    .forEach((line) => {
      const match = line.match(/^([^:]+):\s*\$?([\d.]+)$/);

      if (match) {
        prices[match[1].trim().toLowerCase()] = Number(match[2]);
      }
    });

  return fallbackColumns.map((column) => ({
    ...column,
    price:
      prices[column.name.toLowerCase()] ??
      getInstagramPrice(column.name, column.defaultPrice),
  }));
};

const parseTikTokPricing = (text, fallbackColumns) => {
  const prices = {};

  let section = 'monthly';

  String(text || '')
    .split('\n')
    .map((line) => line.trim())
    .forEach((line) => {
      if (!line) return;

      if (/^monthly\s*:?\s*$/i.test(line)) {
        section = 'monthly';
        return;
      }

      if (/^yearly\s*:?\s*$/i.test(line)) {
        section = 'yearly';
        return;
      }

      const match = line.match(/^([^:]+):\s*\$?([\d.]+)$/);

      if (match) {
        const name = match[1].trim().toLowerCase();
        prices[`${section}-${name}`] = Number(match[2]);
      }
    });

  return fallbackColumns.map((column) => ({
    ...column,
    price:
      prices[`monthly-${column.name.toLowerCase()}`] ??
      getTikTokPrice(column.name, column.defaultPrice),
  }));
};

const PlanCompare = () => {
  const [columns, setColumns] = useState(
    COLUMNS.map((column) => ({
      ...column,
      price: column.defaultPrice,
    }))
  );

  useEffect(() => {
    const loadPricing = async () => {
      try {
        const settings = await api.getPublicSettings();

        const instagramColumns = COLUMNS.filter(
          (column) => column.platform === 'Instagram'
        );

        const tiktokColumns = COLUMNS.filter(
          (column) => column.platform === 'TikTok'
        );

        const updatedInstagram = parseInstagramPricing(
          settings?.instagram_pricing,
          instagramColumns
        );

        const updatedTikTok = parseTikTokPricing(
          settings?.tiktok_pricing,
          tiktokColumns
        );

        const updated = COLUMNS.map((column) => {
          const source =
            column.platform === 'Instagram'
              ? updatedInstagram
              : updatedTikTok;

          const match = source.find(
            (item) => item.key === column.key
          );

          return {
            ...column,
            price: match?.price ?? column.defaultPrice,
          };
        });

        setColumns(updated);
      } catch (error) {
        // Keep default pricing if public settings are unavailable.
      }
    };

    loadPricing();
  }, []);

  const buildHref = (column) => {
    const price = Number(column.price || column.defaultPrice);

    if (column.platform === 'Instagram') {
      const plan = column.name.toLowerCase();

      return `/get-started?plan=${plan}&platform=instagram&price=${price}&name=${column.name}&range=${encodeURIComponent(
        column.range
      )}`;
    }

    const plan = `tiktok-${column.name.toLowerCase()}`;

    return `/get-started?plan=${plan}&platform=tiktok&price=${price}&name=${encodeURIComponent(
      `TikTok ${column.name}`
    )}&range=${encodeURIComponent(column.range)}&billing=monthly`;
  };

  return (
    <section className="py-24 bg-gradient-to-b from-white to-slate-50/60">
      <div className="max-w-6xl mx-auto px-5 lg:px-8">
        <div className="text-center max-w-3xl mx-auto mb-12">
          <p className="text-sm font-bold tracking-widest uppercase plixy-gradient-text">
            Compare Plans
          </p>

          <h2 className="mt-3 text-4xl md:text-5xl font-extrabold text-[#0a1930] leading-tight">
            Not sure which plan fits?{' '}
            <span className="plixy-gradient-text">
              See them side-by-side.
            </span>
          </h2>

          <p className="mt-3 text-slate-600">
            Every plan includes real, organic followers. Bigger tiers unlock
            more growth per month + premium support.
          </p>
        </div>

        <div className="rounded-3xl border border-slate-200 bg-white shadow-sm overflow-hidden">

          {/* Header row */}
          <div className="hidden md:grid grid-cols-5 gap-0 bg-slate-50 border-b border-slate-200">
            <div className="p-5 text-xs font-bold uppercase tracking-widest text-slate-500">
              Feature
            </div>

            {columns.map((c) => (
              <div
                key={c.key}
                className={`p-5 border-l border-slate-200 relative ${
                  c.highlight ? 'bg-white' : ''
                }`}
              >
                {c.highlight && (
                  <div className="absolute top-2 right-2 text-[10px] font-bold plixy-gradient-text uppercase tracking-widest">
                    Popular
                  </div>
                )}

                <div className="flex items-center gap-2">
                  <IconChip type={c.icon} />

                  <div className="text-xs font-bold uppercase tracking-widest text-slate-500">
                    {c.platform}
                  </div>
                </div>

                <div className="mt-2 font-extrabold text-[#0a1930] text-lg">
                  {c.name}
                </div>

                <div className="text-slate-600 text-sm font-semibold">
                  ${c.price}
                  {c.platform === 'TikTok' && (
                    <span className="text-slate-400"> /mo</span>
                  )}
                </div>
              </div>
            ))}
          </div>

          {/* Data rows */}
          <div>
            {ROWS.map((row, i) => (
              <div
                key={row.label}
                className={`grid grid-cols-2 md:grid-cols-5 ${
                  i % 2 === 0 ? 'bg-white' : 'bg-slate-50/50'
                } border-b border-slate-100 last:border-b-0`}
              >
                <div className="col-span-2 md:col-span-1 p-4 md:p-5 text-sm font-semibold text-[#0a1930]">
                  {row.label}
                </div>

                {row.values.map((v, k) => (
                  <div
                    key={k}
                    className="p-4 md:p-5 md:border-l border-slate-100 flex items-center gap-2"
                  >
                    <span className="md:hidden text-[10px] font-bold uppercase tracking-widest text-slate-400">
                      {columns[k].platform} {columns[k].name}:
                    </span>

                    {typeof v === 'boolean' ? (
                      v ? (
                        <span className="inline-flex items-center justify-center w-6 h-6 rounded-full bg-emerald-100">
                          <Check
                            className="w-3.5 h-3.5 text-emerald-600"
                            strokeWidth={3}
                          />
                        </span>
                      ) : (
                        <span className="inline-flex items-center justify-center w-6 h-6 rounded-full bg-slate-100">
                          <X
                            className="w-3.5 h-3.5 text-slate-400"
                            strokeWidth={3}
                          />
                        </span>
                      )
                    ) : (
                      <span className="text-sm font-bold text-[#0a1930]">
                        {v}
                      </span>
                    )}
                  </div>
                ))}
              </div>
            ))}
          </div>

          {/* CTA row */}
          <div className="hidden md:grid grid-cols-5 border-t border-slate-200 bg-slate-50">
            <div className="p-5"></div>

            {columns.map((c) => (
              <div
                key={c.key}
                className="p-5 border-l border-slate-200"
              >
                <Link
                  to={buildHref(c)}
                  className={`w-full inline-flex items-center justify-center gap-1 py-2.5 rounded-full font-bold text-sm plixy-dark-btn ${
                    c.highlight ? 'plixy-gradient-border' : ''
                  }`}
                >
                  Choose {c.name}
                  <ChevronRight className="w-4 h-4" />
                </Link>
              </div>
            ))}
          </div>

          {/* Mobile CTA list */}
          <div className="md:hidden p-4 grid gap-2 bg-slate-50 border-t border-slate-200">
            {columns.map((c) => (
              <Link
                key={c.key}
                to={buildHref(c)}
                className="plixy-dark-btn inline-flex items-center justify-center gap-1 py-3 rounded-full font-bold text-sm"
              >
                Choose {c.platform} {c.name} — ${c.price}
                {c.platform === 'TikTok' ? '/mo' : ''}
              </Link>
            ))}
          </div>

        </div>
      </div>
    </section>
  );
};

export default PlanCompare;
